import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { format, parse, isValid } from "date-fns";

import { THEME_COLOR, THEME_COLOR3 } from "../constants";

import styles from "./DeliveryTakeAwayWindows.module.css";

const theme = createTheme({
  palette: {
    gray: {
      main: THEME_COLOR3,
    },
    red: {
      main: "#FF0000",
    },
  },
});

const TimePickerSlotProps = {
  actionBar: {
    sx: {
      "& .MuiButton-root": {
        color: "#000",
        fontFamily: "GilroySemiBold",
      },
    },
  },
  digitalClockSectionItem: {
    sx: {
      fontSize: 13,
      fontFamily: "GilroySemiBold",
      "&.Mui-selected": {
        backgroundColor: `${THEME_COLOR}`,
        color: "#000",
        "&:hover": {
          backgroundColor: `${THEME_COLOR}`,
          color: "#000",
        },
      },
    },
  },
  textField: {
    color: "gray",
    variant: "standard",
    InputLabelProps: {
      style: { fontFamily: "GilroyRegular" },
    },
    inputProps: {
      readOnly: true,
      sx: {
        height: 20,
        fontSize: 12,
        fontFamily: "GilroySemiBold",
        padding: "4px 8px",
      },
    },
    sx: {
      svg: { color: "#000", fontSize: 16 },
      "& .MuiButtonBase-root": {
        width: 24,
        height: 24,
      },
      "& .MuiInputBase-root": {
        minHeight: "32px",
        height: "32px",
      },
      "& .MuiInputLabel-root": {
        fontSize: "12px",
        transform: "translate(14px, 8px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
    },
  },
};

// Helper: Format Date to time string "HH:mm"
const formatTime = (date) => {
  if (!date || !isValid(date)) return null;
  return format(date, "HH:mm");
};

// Equivalent to your parseTime
const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const parsed = parse(timeStr, "HH:mm", new Date());
  return isValid(parsed) ? parsed : null;
};

// Helper: Create default day timing object
const createDefaultDayTiming = () => ({
  isOpen: true,
  timings: [{ from: null, to: null }],
});

const DeliveryTakeAwayWindows = ({
  deliveryWindow = {},
  takeAwayWindow = {},
  onDeliveryWindowChange,
  onTakeAwayWindowChange,
  errors = {},
}) => {
  // State for "All Days" times (single slot for bulk apply)
  const [allDaysTimes, setAllDaysTimes] = useState({
    delivery: { startTime: null, endTime: null },
    takeaway: { startTime: null, endTime: null },
  });

  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // Update parent state for a specific day
  const updateWindow = (day, data, type) => {
    if (type === "delivery") {
      onDeliveryWindowChange({ ...deliveryWindow, [day]: data });
    } else {
      onTakeAwayWindowChange({ ...takeAwayWindow, [day]: data });
    }
  };

  // Update isOpen status for a day
  const toggleDayOpen = (day, type) => {
    const currentWindow = type === "delivery" ? deliveryWindow : takeAwayWindow;
    const dayData = currentWindow[day] || createDefaultDayTiming();

    updateWindow(day, { ...dayData, isOpen: !dayData.isOpen }, type);
  };

  // Add a new time slot to a day
  const addTimeSlot = (day, type) => {
    const currentWindow = type === "delivery" ? deliveryWindow : takeAwayWindow;
    const dayData = currentWindow[day] || createDefaultDayTiming();

    const newTimings = [...dayData.timings, { from: null, to: null }];

    updateWindow(day, { ...dayData, timings: newTimings }, type);
  };

  // Remove a time slot from a day
  const removeTimeSlot = (day, slotIndex, type) => {
    const currentWindow = type === "delivery" ? deliveryWindow : takeAwayWindow;
    const dayData = currentWindow[day];

    if (!dayData || !dayData.timings) return;

    const newTimings = dayData.timings.filter((_, idx) => idx !== slotIndex);

    if (newTimings.length === 0) {
      // If no slots left, set isOpen to false
      updateWindow(day, { ...dayData, timings: [], isOpen: false }, type);
    } else {
      updateWindow(day, { ...dayData, timings: newTimings }, type);
    }
  };

  // Update a specific time slot
  const updateTimeSlot = (day, slotIndex, field, value, type) => {
    const currentWindow = type === "delivery" ? deliveryWindow : takeAwayWindow;
    const dayData = currentWindow[day] || createDefaultDayTiming();

    const newTimings = [...dayData.timings];
    newTimings[slotIndex] = {
      ...newTimings[slotIndex],
      [field]: formatTime(value),
    };

    updateWindow(day, { ...dayData, timings: newTimings }, type);
  };

  // Apply all days times to ALL days
  const applyAllDays = (type) => {
    const allDaysData = allDaysTimes[type];
    const formattedFrom = formatTime(allDaysData.startTime);
    const formattedTo = formatTime(allDaysData.endTime);

    const updatedWindow = {};
    const defaultTiming = createDefaultDayTiming();
    defaultTiming.timings[0] = { from: formattedFrom, to: formattedTo };

    daysOfWeek.forEach((day) => {
      updatedWindow[day] = { ...defaultTiming };
    });

    if (type === "delivery") {
      onDeliveryWindowChange(updatedWindow);
    } else {
      onTakeAwayWindowChange(updatedWindow);
    }
  };

  // Render time slots for a day
  const renderTimeSlots = (day, dayData, type) => {
    if (!dayData || !dayData.timings) return null;

    return (
      <Grid container spacing={0.5}>
        {/* Header row with From/To labels */}
        <Grid item xs={12}>
          <Grid container spacing={0.5}>
            <Grid item xs={5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "GilroyRegular",
                  color: "#888",
                  mb: 0.5,
                  pl: "14px",
                }}
              >
                From
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "GilroyRegular",
                  color: "#888",
                  mb: 0.5,
                  pl: "14px",
                }}
              >
                To
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {dayData.timings.map((slot, index) => (
          <React.Fragment key={index}>
            <Grid item xs={5}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label=""
                    value={parseTime(slot.from)}
                    format="HH:mm"
                    size="small"
                    onChange={(val) =>
                      updateTimeSlot(day, index, "from", val, type)
                    }
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>

            <Grid item xs={5}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label=""
                    value={parseTime(slot.to)}
                    format="HH:mm"
                    size="small"
                    onChange={(val) =>
                      updateTimeSlot(day, index, "to", val, type)
                    }
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>

            {dayData.timings.length > 1 && (
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => removeTimeSlot(day, index, type)}
                  sx={{ color: "#999" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </React.Fragment>
        ))}

        {/* Add slot button */}
        <Grid item xs={12}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addTimeSlot(day, type)}
            sx={{
              color: THEME_COLOR,
              fontFamily: "GilroySemiBold",
              fontSize: "11px",
              mt: 0.5,
            }}
          >
            Add Time Slot
          </Button>
        </Grid>
      </Grid>
    );
  };

  // Render a single day row
  const renderDay = (day, type) => {
    const currentWindow = type === "delivery" ? deliveryWindow : takeAwayWindow;
    const dayData = currentWindow[day];
    const isActive = dayData !== null && dayData.isOpen;
    const dayName = day.charAt(0).toUpperCase() + day.slice(1);

    return (
      <Grid
        container
        key={day}
        mb={0.5}
        p={1}
        sx={{
          border: "1px solid #ddd",
          borderRadius: "6px",
          backgroundColor: "#fff",
        }}
      >
        {/* Day name and toggle */}
        <Grid container item xs={12} alignItems="center" mb={0.5}>
          <Grid item xs={8}>
            <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "12px" }}>
              {dayName}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <ThemeProvider theme={theme}>
              <Switch
                checked={isActive}
                onChange={() => toggleDayOpen(day, type)}
                color="red"
                size="small"
                sx={{
                  "& .MuiSwitch-track": {
                    height: 12,
                    backgroundColor: THEME_COLOR3 + " !important",
                    marginTop: -0.3,
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#999",
                    width: 16,
                    height: 16,
                  },
                  "& .Mui-checked .MuiSwitch-thumb": {
                    backgroundColor: "red !important",
                  },
                }}
              />
            </ThemeProvider>
          </Grid>
        </Grid>

        {/* Time slots when active */}
        {isActive && renderTimeSlots(day, dayData, type)}

        {/* Show "Closed" when day is null or isOpen is false but not null */}
        {!isActive && dayData !== null && (
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "GilroyRegular",
                fontSize: "11px",
                color: "#999",
              }}
            >
              Closed
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  // Render All Days section
  const renderAllDays = (type) => {
    const allDaysData = allDaysTimes[type];

    return (
      <Grid
        container
        mb={1}
        p={1}
        sx={{
          border: "1px solid #ddd",
          borderRadius: "6px",
          backgroundColor: "#f5f5f5",
        }}
      >
        {/* All Days header */}
        <Grid container item xs={12} alignItems="center" mb={0.5}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              All Days
            </Typography>
          </Grid>
        </Grid>

        {/* Time pickers for All Days */}
        <Grid container spacing={0.5}>
          <Grid item xs={5}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Start"
                  value={allDaysData.startTime}
                  format="HH:mm"
                  size="small"
                  onChange={(val) =>
                    setAllDaysTimes((prev) => ({
                      ...prev,
                      [type]: { ...prev[type], startTime: val },
                    }))
                  }
                  slotProps={TimePickerSlotProps}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </Grid>

          <Grid item xs={5}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="End"
                  value={allDaysData.endTime}
                  format="HH:mm"
                  size="small"
                  onChange={(val) =>
                    setAllDaysTimes((prev) => ({
                      ...prev,
                      [type]: { ...prev[type], endTime: val },
                    }))
                  }
                  slotProps={TimePickerSlotProps}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </Grid>

          <Grid item xs={2}>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={() => applyAllDays(type)}
              sx={{
                backgroundColor: THEME_COLOR,
                color: "#000",
                fontFamily: "GilroySemiBold",
                fontSize: "10px",
                height: "32px",
                minWidth: "60px",
                "&:hover": {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.8,
                },
              }}
            >
              Apply All
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box className={styles?.["main-view"]}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontFamily: "GilroySemiBold", mb: 2 }}>
            Delivery Windows
          </Typography>
          {renderAllDays("delivery")}
          {daysOfWeek.map((day) => renderDay(day, "delivery"))}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontFamily: "GilroySemiBold", mb: 2 }}>
            Take Away Windows
          </Typography>
          {renderAllDays("takeaway")}
          {daysOfWeek.map((day) => renderDay(day, "takeaway"))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeliveryTakeAwayWindows;
